import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthResponse } from "@/api/types";

const STORAGE_KEY = "submitterai.session";

type SessionState = {
  token: string | null;
  userId: string | null;
  email: string | null;
  expiresAt: number | null; // unix seconds
  refreshToken: string | null;
  refreshExpiresAt: number | null; // unix seconds
  hasHydrated: boolean;
  setSession: (r: AuthResponse) => void;
  clear: () => void;
  isValid: () => boolean;
  hasValidRefresh: () => boolean;
  needsRefresh: () => boolean;
  setHasHydrated: (hasHydrated: boolean) => void;
};

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      token: null,
      userId: null,
      email: null,
      expiresAt: null,
      refreshToken: null,
      refreshExpiresAt: null,
      hasHydrated: false,
      setSession: (r) =>
        set({
          token: r.access_token,
          userId: r.user_id,
          email: r.email,
          expiresAt: r.expires_at,
          refreshToken: r.refresh_token,
          refreshExpiresAt: r.refresh_expires_at,
        }),
      clear: () =>
        set({
          token: null,
          userId: null,
          email: null,
          expiresAt: null,
          refreshToken: null,
          refreshExpiresAt: null,
        }),
      isValid: () => {
        const { token, expiresAt } = get();
        if (!token || !expiresAt) return false;
        return expiresAt * 1000 > Date.now();
      },
      hasValidRefresh: () => {
        const { refreshToken, refreshExpiresAt } = get();
        if (!refreshToken || !refreshExpiresAt) return false;
        return refreshExpiresAt * 1000 > Date.now();
      },
      // True whenever we hold a usable refresh token. The session slides on every
      // visit: each full page load exchanges it for a fresh 30-day access +
      // refresh token, so a returning user only has to log in again once they've
      // been away longer than the refresh-token lifetime.
      needsRefresh: () => get().hasValidRefresh(),
      setHasHydrated: (hasHydrated) => set({ hasHydrated }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        token: s.token,
        userId: s.userId,
        email: s.email,
        expiresAt: s.expiresAt,
        refreshToken: s.refreshToken,
        refreshExpiresAt: s.refreshExpiresAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
          // Keep the session if either the access token is still valid OR a
          // valid refresh token can revive it; only clear when both are dead.
          if (!state.isValid() && !state.hasValidRefresh()) {
            state.clear();
          }
        }
      },
    },
  ),
);

// Cross-tab sync: when another tab rotates the token (or logs out), pull the
// change into this tab so all tabs converge on the latest session instead of
// each holding — and re-refreshing — a stale token.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      void useSession.persist.rehydrate();
    }
  });
}
